import { RJSFSchema } from '@rjsf/utils';
import { all as allCountries } from 'country-codes-list';
import {
  KnownKioskHardwareConfigurationDtoCameraDirection,
  KnownKioskHardwareConfigurationDtoCameraDriver,
  KnownKioskHardwareConfigurationDtoCameraRotation,
  KnownKioskHardwareConfigurationDtoLockerType,
  KnownKioskHardwareConfigurationDtoScannerMode,
  KnownKioskModeDtoType,
  KnownKioskSettingsDtoExternalFlow
} from '@groundfloor-platform/mgmt-service-g-sdk';

const countries = allCountries().sort((a, b) => a.countryNameEn.localeCompare(b.countryNameEn));

const yesNo = {
  type: 'string' as const,
  oneOf: [
    { type: 'string' as const, title: 'Yes', enum: ['yes'] },
    { type: 'string' as const, title: 'No', enum: ['no'] }
  ]
};

export const kioskSettingsFormSchema: RJSFSchema = {
  properties: {
    iotHubId: {
      type: 'string',
      title: 'IoT Hub ID',
      readOnly: true
    },
    name: {
      type: 'string',
      title: 'Kiosk name'
    },
    mode: {
      type: 'string',
      title: 'Mode',
      // `default` also stops the RJSF material-ui SelectWidget rendering a blank placeholder option.
      default: KnownKioskModeDtoType.InService,
      oneOf: [
        { type: 'string' as const, title: 'In service', enum: [KnownKioskModeDtoType.InService] },
        { type: 'string' as const, title: 'Coming soon', enum: [KnownKioskModeDtoType.ComingSoon] },
        { type: 'string' as const, title: 'Hardware testing', enum: [KnownKioskModeDtoType.HardwareTesting] },
        { type: 'string' as const, title: 'Out of order', enum: [KnownKioskModeDtoType.OutOfOrder] }
      ]
    },
    showInternalUserButton: {
      ...yesNo,
      title: 'Show internal user button',
      default: 'no'
    },
    internalUserButtonText: {
      type: 'string',
      title: 'Text (eg. Collect/Residents)'
    },
    showExternalUserButton: {
      ...yesNo,
      title: 'Show external user button',
      default: 'no'
    },
    externalUserButtonText: {
      type: 'string',
      title: 'Text (eg. Deliver/Couriers)'
    },
    externalFlow: {
      type: 'string',
      title: 'External user flow',
      // Matches the static `/kiosks/settings/defaults` value; the settings endpoint always returns the real value.
      default: KnownKioskSettingsDtoExternalFlow.CourierLogin,
      description: 'What screen to display first when the external button is pressed',
      oneOf: [
        {
          type: 'string' as const,
          title: 'Login',
          enum: [KnownKioskSettingsDtoExternalFlow.CourierLogin]
        },
        {
          type: 'string' as const,
          title: 'Delivery type',
          enum: [KnownKioskSettingsDtoExternalFlow.DeliveryType]
        }
      ]
    },
    scannerMode: {
      type: 'string',
      title: 'Scanner mode',
      default: KnownKioskHardwareConfigurationDtoScannerMode.Cdc,
      oneOf: [
        {
          type: 'string' as const,
          title: 'CDC (Android default)',
          enum: [KnownKioskHardwareConfigurationDtoScannerMode.Cdc]
        },
        { type: 'string' as const, title: 'Camera', enum: [KnownKioskHardwareConfigurationDtoScannerMode.Camera] },
        { type: 'string' as const, title: 'HID', enum: [KnownKioskHardwareConfigurationDtoScannerMode.Hid] },
        { type: 'string' as const, title: 'None', enum: [KnownKioskHardwareConfigurationDtoScannerMode.None] }
      ]
    },
    cameraEnabled: {
      type: 'boolean',
      title: 'Camera',
      default: true,
      oneOf: [
        { type: 'boolean' as const, title: 'Enabled', enum: [true] },
        { type: 'boolean' as const, title: 'Disabled', enum: [false] }
      ]
    },
    cameraDriver: {
      type: 'string',
      title: 'Camera driver',
      default: KnownKioskHardwareConfigurationDtoCameraDriver.Default,
      oneOf: [
        {
          type: 'string' as const,
          title: 'Default',
          enum: [KnownKioskHardwareConfigurationDtoCameraDriver.Default]
        },
        { type: 'string' as const, title: 'Native', enum: [KnownKioskHardwareConfigurationDtoCameraDriver.Native] }
      ]
    },
    cameraDirection: {
      type: 'string',
      title: 'Camera direction',
      default: KnownKioskHardwareConfigurationDtoCameraDirection.External,
      oneOf: [
        { type: 'string' as const, title: 'Front', enum: [KnownKioskHardwareConfigurationDtoCameraDirection.Front] },
        { type: 'string' as const, title: 'Back', enum: [KnownKioskHardwareConfigurationDtoCameraDirection.Back] },
        {
          type: 'string' as const,
          title: 'External',
          enum: [KnownKioskHardwareConfigurationDtoCameraDirection.External]
        },
        { type: 'string' as const, title: 'None', enum: [KnownKioskHardwareConfigurationDtoCameraDirection.None] }
      ]
    },
    cameraRotation: {
      type: 'string',
      title: 'Camera rotation',
      default: KnownKioskHardwareConfigurationDtoCameraRotation.None,
      oneOf: [
        {
          type: 'string' as const,
          title: 'None (0°)',
          enum: [KnownKioskHardwareConfigurationDtoCameraRotation.None]
        },
        { type: 'string' as const, title: '90°', enum: [KnownKioskHardwareConfigurationDtoCameraRotation.Ninety] },
        {
          type: 'string' as const,
          title: '180°',
          enum: [KnownKioskHardwareConfigurationDtoCameraRotation.OneHundredEighty]
        },
        {
          type: 'string' as const,
          title: '270°',
          enum: [KnownKioskHardwareConfigurationDtoCameraRotation.TwoHundredSeventy]
        }
      ]
    },
    lockerType: {
      type: 'string',
      title: 'Locker type',
      default: KnownKioskHardwareConfigurationDtoLockerType.None,
      oneOf: [
        { type: 'string' as const, title: 'HZDC', enum: [KnownKioskHardwareConfigurationDtoLockerType.Hzdc] },
        { type: 'string' as const, title: 'Kerong', enum: [KnownKioskHardwareConfigurationDtoLockerType.Kerong] },
        { type: 'string' as const, title: 'Fake', enum: [KnownKioskHardwareConfigurationDtoLockerType.Fake] },
        { type: 'string' as const, title: 'None', enum: [KnownKioskHardwareConfigurationDtoLockerType.None] }
      ]
    },
    internalUserSingular: {
      type: 'string',
      title: 'Internal user label (singular)'
    },
    internalUserPlural: {
      type: 'string',
      title: 'Internal user label (plural)'
    },
    externalUserSingular: {
      type: 'string',
      title: 'External user label (singular)'
    },
    externalUserPlural: {
      type: 'string',
      title: 'External user label (plural)'
    },
    scanQrCodeHeading: {
      type: 'string',
      title: 'Scan QR code heading'
    },
    scanQrCodeSubheading: {
      type: 'string',
      title: 'Scan QR code subheading'
    },
    externalLoginPhoneLoginHeading: {
      type: 'string',
      title: 'Phone login heading'
    },
    externalLoginPhoneLoginSubheading: {
      type: 'string',
      title: 'Phone login subheading'
    },
    collectionCodeSubHeading: {
      type: 'string',
      title: 'Collection code subheading'
    },
    noDoorsAvailableSubheading: {
      type: 'string',
      title: 'No doors available subheading'
    },
    csPhoneInternal: {
      type: 'string',
      title: 'Customer service phone (internal users)',
      // Only digits, spaces, +, () and - are allowed; blank is allowed (field is optional).
      pattern: '^[0-9+ ()-]*$'
    },
    csPhoneExternal: {
      type: 'string',
      title: 'Customer service phone (external users)',
      // Only digits, spaces, +, () and - are allowed; blank is allowed (field is optional).
      pattern: '^[0-9+ ()-]*$'
    },
    groundfloorPortalAddress: {
      type: 'string',
      title: 'Groundfloor portal address',
      // Must be an absolute http(s):// URL with no whitespace, e.g. not a bare hostname; blank is allowed.
      pattern: '^(https?://\\S+)?$'
    },
    countryCode: {
      type: 'string',
      title: 'Country code',
      // `default` also stops the RJSF material-ui SelectWidget rendering a blank placeholder option.
      default: 'AU',
      oneOf: countries.map((c) => ({
        type: 'string' as const,
        title: `${c.countryNameEn} (+${c.countryCallingCode})`,
        enum: [c.countryCode]
      }))
    }
  },
  required: ['showInternalUserButton', 'showExternalUserButton', 'scannerMode', 'cameraEnabled', 'cameraDirection']
};
